import QuoteProductsTable from '../components/QuoteProductsTable';
import { buildQuoteProducts, buildQuotePayload, quoteProductsSchema, quoteFormErrors } from '../utils/quoteProducts';
import { clearQuoteDataCache } from '../utils/quoteRequests';
import React, {useState, useEffect, useRef, useMemo} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Form } from '@themesberg/react-bootstrap';
import DatePickerField from '../components/DatePickerField';
import axios from "axios";
import { API_URL, END_POINT_QUOTES, END_POINT_PRINCIPAL, END_POINT_ADMIN, END_POINT_BRANCHES, END_POINT_COMPANY, END_POINT_CUSTOMERS, END_POINT_PRODUCT_CATEGORIES } from "../const";
import { useParams, useNavigate } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from "../utils/toast";
import isEqual from "lodash.isequal";
import EditFormLayout from "../components/EditFormLayout";
import EditFormActions from "../components/EditFormActions";
import {
  FormikSearchableSelect,
  buildSelectOptions,
} from "../components/SearchableSelect";

export const EditQuoteForm = () => {

    const { id } = useParams();

    const navigate = useNavigate();

    const rawToken = sessionStorage.getItem('quotetoken');
    const token = rawToken?.replace(/^"|"$/g, '');

    const [principal, setPrincipal] = useState([]);
    const [admin, setAdmin] = useState([]);
    const [branch, setBranch] = useState([]);
    const [company, setCompany] = useState([]);
    const [customer, setCustomers] = useState([]);
    const [product, setProduct] = useState([]);

    const [quote, setQuote] = useState(null);

    const [loading, setLoading] = useState(true); // true initially to show loader
    
    const [isModified, setIsModified] = useState(false);

    useEffect(() => {
        const fetchQuoteDetails = async () => {
          try {
            const response = await axios.get(`${API_URL + END_POINT_QUOTES}/${id}`, {
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
            setQuote(response.data.data);
          } catch (error) {
            console.error('Error fetching company:', error);
          } finally {
            setLoading(false); // hide loader in all cases
          }
        };
    
        fetchQuoteDetails();
    }, [id, token]);

    // Remove null/undefined or trim strings if needed
    const buildInitialValues = (quote) => ({
      system_quote_no: quote?.system_quote_no || "",
      offer_date: quote?.offer_date || "",
      customer_id: String(quote?.customer_id || "0"),
      place: quote?.place || "",
      company_id: String(quote?.company_id || "0"),
      branch_id: String(quote?.branch_id || "0"),
      principal_id: String(quote?.principal_id || "0"),
      products: buildQuoteProducts(quote),
      category_id: String(quote?.category_id || "0"),
      se_id: String(quote?.se_id || "0"),
      expected_date: quote?.expected_date || "",
      status: quote?.status || "0",
      remarks: quote?.remarks || "",
    });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(quote),
    validationSchema: Yup.object({
        // system_quote_no: Yup.string().required("System quote number is required"),
        system_quote_no: Yup.string()
          .matches(/^[0-9]+$/, "Only numbers are allowed"),
        place: Yup.string().required("Place is required"),
        products: quoteProductsSchema,
        offer_date: Yup.string().required("Offer date is required"),
        expected_date: Yup.string().required("Expected date is required"),
        customer_id: Yup.string()
            .notOneOf(["0"], "Please select a valid Customer")
            .required("Customer is required"),
        company_id: Yup.string()
            .notOneOf(["0"], "Please select a valid Company")
            .required("Company is required"),
        category_id: Yup.string()
            .notOneOf(["0"], "Please select a valid product category")
            .required("Product category is required"),
        branch_id: Yup.string()
          .notOneOf(["0"], "Please select a valid Branch")
          .required("Branch is required"),
        principal_id: Yup.string()
          .notOneOf(["0"], "Please select a valid Principal")
          .required("Principal is required"),
        se_id: Yup.string()
            .notOneOf(["0"], "Please select a valid Sales Executive")
            .required("Sales Executive is required"),
        status: Yup.string()
            .notOneOf(["0"], "Please select a valid status")
            .required("Status is required"),
        // remarks: Yup.string()
        //     .required("Remarks is required")
    }),
    onSubmit: async (values, { setErrors }) => {
      try {
        const response = await axios.put(`${API_URL + END_POINT_QUOTES}/${id}`, buildQuotePayload(values),
            {
                headers: {
                    // 'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json',
                    Authorization: `Bearer ${token}`,
                }
            }
        );
        clearQuoteDataCache();
        showSuccessToast(response.data.message);
        setIsModified(false);
        navigate('/quotes');
      } catch (error) {
        if (error.response?.data?.errors) setErrors(quoteFormErrors(error.response.data.errors));
        showErrorToast(error.response?.data?.message || 'Failed to update Quotes.')
      }
    }
  });

  const originalValuesRef = useRef(null);

  // Update ref only when quote changes (and not on every render)
  useEffect(() => {
    if (quote) {
      originalValuesRef.current = buildInitialValues(quote);
    }
  }, [quote]);

  useEffect(() => {
    if (originalValuesRef.current) {
      const hasChanged = !isEqual(formik.values, originalValuesRef.current);
      setIsModified(hasChanged);
    }
  }, [formik.values]);


  const handleOfferDateChange = (date) => {
    formik.setFieldValue("offer_date", date);
  };

  const handleExpectedDateChange = (date) => {
    formik.setFieldValue("expected_date", date);
  };

    
  // principal list
    useEffect(() => {
      const fetchPrincipallist = async () => {
        try {
  
          const response = await axios.get(API_URL + END_POINT_PRINCIPAL, {
            headers: {
              // 'Content-Type': 'multipart/form-data',
              'Accept': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          });
  
          if (response.data.status && Array.isArray(response.data.data)) {
            setPrincipal(response.data.data);
          }
  
        } catch (error) {
          console.error('Error fetching tasks:', error);
        }
  
      };
  
      const timeout = setTimeout(() => {
        fetchPrincipallist();
      });
      return () => clearTimeout(timeout);
  
    }, []);


    // Admin list
    useEffect(() => {
        const fetchAdminlist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_ADMIN, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data.status && Array.isArray(response.data.data)) {
              setAdmin(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching tasks:', error);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchAdminlist();
        });
        return () => clearTimeout(timeout);
    
      }, []);

      
    // Branches list  
    useEffect(() => {
        const fetchBranchlist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_BRANCHES, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data && response.data.status) {
              setBranch(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching tasks:', error);
            // setIsFetching(false);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchBranchlist();
        });
        return () => clearTimeout(timeout);
    
    }, []);


    // company list
    useEffect(() => {
        const fetchCompanylist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_COMPANY, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
                
              }
            });
    
            if (response.data?.status && response.data.data) {
              setCompany(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching tasks:', error);
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchCompanylist();
        });
        return () => clearTimeout(timeout);
    
    }, []);

    // customers list
    useEffect(() => {
        const fetchCustomerslist = async () => {
          try {
    
            const response = await axios.get(API_URL + END_POINT_CUSTOMERS, {
              headers: {
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });
    
            if (response.data.status) {
              setCustomers(response.data.data);
            }
    
          } catch (error) {
            console.error('Error fetching tasks:', error);
           
          }
    
        };
    
        const timeout = setTimeout(() => {
          fetchCustomerslist();
        });
        return () => clearTimeout(timeout);
    
      }, []);


    useEffect(() => {
        const fetchProductslist = async () => {
            try {
          
                const response = await axios.get(API_URL + END_POINT_PRODUCT_CATEGORIES, {
                    headers: {
                      'Accept': 'application/json',
                      Authorization: `Bearer ${token}`,
                    }
                });
          
                if (response.data.status) {
                    setProduct(response.data.data);
                }
          
            } catch (error) {
                console.error('Error fetching product:', error);     
            }

        };

        const timeout = setTimeout(() => {
          fetchProductslist();
        });
        return () => clearTimeout(timeout);

    }, []);


  const customerOptions = useMemo(() => buildSelectOptions(customer), [customer]);
  const companyOptions = useMemo(() => buildSelectOptions(company), [company]);
  const branchOptions = useMemo(() => buildSelectOptions(branch), [branch]);
  const principalOptions = useMemo(() => buildSelectOptions(principal), [principal]);
  const categoryOptions = useMemo(() => buildSelectOptions(product), [product]);
  const adminOptions = useMemo(() => buildSelectOptions(admin), [admin]);
  const statusOptions = useMemo(
    () =>
      ['Closed', 'Live', 'Lost', 'Won'].map((status) => ({
        value: status,
        label: status,
      })),
    []
  );

  return (
    <EditFormLayout
      title="Edit Quote"
      subtitle={quote?.offer_no ? `Offer no: ${quote.offer_no}` : 'Update quote details'}
      backTo="/quotes"
      loading={loading}
    >
      <Form noValidate onSubmit={formik.handleSubmit}>
            <Row>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="system_quote_no">
                  <Form.Label>System Quote No.</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="system_quote_no"
                    value={formik.values.system_quote_no}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value.replace(/\D/g, "");
                      formik.setFieldValue("system_quote_no", onlyNumbers);
                    }}
                    isInvalid={formik.touched.system_quote_no && !!formik.errors.system_quote_no}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.system_quote_no}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Group controlId="offer_date">
                  <Form.Label>Offer Date <span className="text-danger">*</span> </Form.Label>
                  <DatePickerField
                    id="offer_date"
                    name="offer_date"
                    value={formik.values.offer_date}
                    onChange={handleOfferDateChange}
                    onBlur={() => formik.setFieldTouched('offer_date', true)}
                    isInvalid={formik.touched.offer_date && formik.errors.offer_date}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.offer_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="customer_id">
                  <Form.Label>Customer <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="customer_id"
                    options={customerOptions}
                    placeholder="Search customer..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="place">
                  <Form.Label>Place <span className="text-danger">*</span> </Form.Label>
                  <Form.Control
                    type="text"
                    {...formik.getFieldProps("place")}
                    alue={formik.values.place}
                    readOnly
                    isInvalid={formik.touched.place && formik.errors.place}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.place}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="company_id">
                  <Form.Label>Company <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="company_id"
                    options={companyOptions}
                    placeholder="Search company..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="branch_id">
                  <Form.Label>Branch <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="branch_id"
                    options={branchOptions}
                    placeholder="Search branch..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="principal_id">
                  <Form.Label>Principal <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="principal_id"
                    options={principalOptions}
                    placeholder="Search principal..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="category_id">
                  <Form.Label>Product Category <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="category_id"
                    options={categoryOptions}
                    placeholder="Search category..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col xs={12} className="mb-3">
                <QuoteProductsTable formik={formik} />
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="se_id">
                  <Form.Label>User<span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="se_id"
                    options={adminOptions}
                    placeholder="Search user..."
                    isDisabled
                  />
                </Form.Group>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Group controlId="expected_date">
                  <Form.Label>Expected Date <span className="text-danger">*</span> </Form.Label>
                  <DatePickerField
                    id="expected_date"
                    name="expected_date"
                    value={formik.values.expected_date}
                    onChange={handleExpectedDateChange}
                    onBlur={() => formik.setFieldTouched('expected_date', true)}
                    isInvalid={formik.touched.expected_date && formik.errors.expected_date}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.expected_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col sm={4} className="mb-3">
                <Form.Group controlId="status">
                  <Form.Label>Status <span className="text-danger">*</span> </Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="status"
                    options={statusOptions}
                    placeholder="Search status..."
                    isDisabled={Boolean(quote?.po_converted_at)}
                  />
                  {quote?.po_converted_at && <Form.Text className="text-muted">Converted quotes remain Won.</Form.Text>}
                </Form.Group>
              </Col>

              <Col sm={12} className="mb-3">
                <Form.Group controlId="remarks">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    // readOnly
                    // type="text"
                    as="textarea"
                    row={3}
                    {...formik.getFieldProps("remarks")}
                    isInvalid={formik.touched.remarks && formik.errors.remarks}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.remarks}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

            </Row>

        <EditFormActions
          backTo="/quotes"
          isDirty={formik.dirty}
          isSubmitting={formik.isSubmitting}
        />
      </Form>
    </EditFormLayout>
  );
};